const TRACK_ENDPOINT = process.env.REACT_APP_TRACK_URL || "/events";
const STORAGE_KEYS = {
  userId: "analytics_uid",
  sessionId: "analytics_sid",
  lastActive: "analytics_last_active",
};
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const FLUSH_INTERVAL_MS = 3000;
const MAX_BUFFER_SIZE = 5;

let eventBuffer = [];
let flushTimer = null;
let initialized = false;

const safeNow = () => Date.now();

const createId = (prefix) => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  const rand = Math.random().toString(16).slice(2);
  return `${prefix}_${safeNow().toString(16)}${rand}`;
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage errors (private mode, blocked storage).
  }
};

const getUserId = () => {
  if (typeof window === "undefined") return "u_unknown";
  const existing = readStorage(window.localStorage, STORAGE_KEYS.userId);
  if (existing) return existing;
  const next = createId("u");
  writeStorage(window.localStorage, STORAGE_KEYS.userId, next);
  return next;
};

const getSessionId = () => {
  if (typeof window === "undefined") return "s_unknown";
  const now = safeNow();
  const lastActive = parseInt(
    readStorage(window.sessionStorage, STORAGE_KEYS.lastActive),
    10
  );
  const expired = Number.isNaN(lastActive) || now - lastActive > IDLE_TIMEOUT_MS;
  let sessionId = readStorage(window.sessionStorage, STORAGE_KEYS.sessionId);
  if (!sessionId || expired) {
    sessionId = createId("s");
    writeStorage(window.sessionStorage, STORAGE_KEYS.sessionId, sessionId);
  }
  writeStorage(window.sessionStorage, STORAGE_KEYS.lastActive, String(now));
  return sessionId;
};

const normalizeString = (value, maxLen) => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
};

const buildEvent = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const base = {
    event_id: payload.event_id || createId("e"),
    user_id: getUserId(),
    session_id: getSessionId(),
    event: payload.event,
    ts: payload.ts || safeNow(),
    page:
      payload.page ||
      (typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : undefined),
    ref:
      payload.ref ||
      (typeof document !== "undefined" ? document.referrer : undefined),
  };

  const event = {
    ...base,
    query: normalizeString(payload.query, 200),
    doc_id: normalizeString(payload.doc_id, 200),
    pos: Number.isFinite(payload.pos) ? payload.pos : undefined,
  };

  return event;
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushEvents("timer");
  }, FLUSH_INTERVAL_MS);
};

const sendPayload = async (events, reason) => {
  if (!events.length) return true;
  const payload = JSON.stringify({ events, reason });
  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    const ok = navigator.sendBeacon(TRACK_ENDPOINT, blob);
    if (ok) return true;
  }
  if (typeof fetch === "undefined") return false;
  const response = await fetch(TRACK_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  });
  return response.ok;
};

const flushEvents = async (reason) => {
  if (!eventBuffer.length) return;
  const events = eventBuffer;
  eventBuffer = [];
  try {
    const ok = await sendPayload(events, reason);
    if (!ok) {
      eventBuffer = events.concat(eventBuffer);
      scheduleFlush();
    }
  } catch (error) {
    eventBuffer = events.concat(eventBuffer);
    scheduleFlush();
  }
};

export const initTracking = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  getUserId();
  getSessionId();

  const handleVisibility = () => {
    if (document.visibilityState === "hidden") {
      flushEvents("visibility");
    }
  };

  window.addEventListener("visibilitychange", handleVisibility);
  window.addEventListener("pagehide", () => flushEvents("pagehide"));
};

export const trackEvent = (payload) => {
  if (typeof window === "undefined") return;
  if (!initialized) initTracking();
  const event = buildEvent(payload);
  if (!event || !event.event) return;
  eventBuffer.push(event);
  if (eventBuffer.length >= MAX_BUFFER_SIZE) {
    flushEvents("buffer");
  } else {
    scheduleFlush();
  }
};

export const flushTracking = () => flushEvents("manual");
